import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createWikiPage,
  deleteWikiPage,
  getWikiPages,
  updateWikiPage,
} from './AdminAPI';

const BLOCK_TYPES = [
  ['text', 'Text'],
  ['h2', 'Heading 2'],
  ['page-link', 'Wiki page link'],
];

const emptyBlock = () => ({
  type: 'text',
  text: '',
  targetSlug: '',
});

const emptyDraft = () => ({
  id: null,
  slug: '',
  title: '',
  blocks: [emptyBlock()],
  published: false,
});

function normalizeBlock(block) {
  return {
    type: String(block?.type ?? block?.Type ?? 'text'),
    text: String(block?.text ?? block?.Text ?? ''),
    targetSlug: String(block?.targetSlug ?? block?.TargetSlug ?? ''),
  };
}

function normalizePage(page) {
  return {
    id: page?.id ?? page?.Id ?? null,
    slug: String(page?.slug ?? page?.Slug ?? ''),
    title: String(page?.title ?? page?.Title ?? ''),
    blocks: (page?.blocks ?? page?.Blocks ?? []).map(normalizeBlock),
    published: Boolean(page?.published ?? page?.Published),
  };
}

function payload(draft) {
  return {
    slug: draft.slug.trim().toLowerCase(),
    title: draft.title.trim(),
    published: draft.published,
    blocks: draft.blocks.map(block => ({
      type: block.type,
      text: block.text.trim(),
      targetSlug: block.type === 'page-link'
        ? block.targetSlug.trim().toLowerCase()
        : '',
    })),
  };
}

export default function AdminWiki() {
  const [pages, setPages] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getWikiPages();
      const loaded = Array.isArray(result) ? result.map(normalizePage) : [];
      setPages(loaded);
      setDraft(loaded[0] ?? emptyDraft());
    } catch {
      setError('The wiki pages could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const linkedPages = useMemo(
    () => pages.filter(page => page.slug !== draft.slug),
    [draft.slug, pages],
  );

  const choose = (page) => {
    setDraft(normalizePage(page));
    setError('');
  };

  const updateBlock = (index, patch) => {
    setDraft(current => ({
      ...current,
      blocks: current.blocks.map((block, blockIndex) => (
        blockIndex === index ? { ...block, ...patch } : block
      )),
    }));
  };

  const moveBlock = (index, direction) => {
    setDraft(current => {
      const target = index + direction;
      if (target < 0 || target >= current.blocks.length) return current;
      const blocks = [...current.blocks];
      [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
      return { ...current, blocks };
    });
  };

  const removeBlock = (index) => {
    setDraft(current => ({
      ...current,
      blocks: current.blocks.filter((_, blockIndex) => blockIndex !== index),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const saved = normalizePage(draft.id
        ? await updateWikiPage(draft.id, payload(draft))
        : await createWikiPage(payload(draft)));
      setPages(current => {
        const exists = current.some(page => page.id === saved.id);
        return (exists
          ? current.map(page => (page.id === saved.id ? saved : page))
          : [...current, saved])
          .sort((left, right) => left.title.localeCompare(right.title));
      });
      setDraft(saved);
    } catch (saveError) {
      setError(saveError?.message
        || 'The wiki page could not be saved. Your edits are unchanged.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft.id || !window.confirm(`Delete wiki page “${draft.title}”?`)) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await deleteWikiPage(draft.id);
      const remaining = pages.filter(page => page.id !== draft.id);
      setPages(remaining);
      setDraft(remaining[0] ?? emptyDraft());
    } catch (deleteError) {
      setError(deleteError?.message || 'The wiki page could not be deleted.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-settingspage admin-wiki">
      <aside aria-label="Wiki page list" className="admin_list">
        <button
          className="button-primary"
          disabled={saving}
          onClick={() => setDraft(emptyDraft())}
          type="button"
        >
          New page
        </button>
        {loading && <p role="status">Loading wiki pages…</p>}
        {!loading && pages.length === 0 && <p>No wiki pages exist yet.</p>}
        {pages.map(page => (
          <button
            className={`listElement${draft.id === page.id ? ' is-active' : ''}`}
            key={page.id}
            onClick={() => choose(page)}
            type="button"
          >
            <strong>{page.title}</strong>
            <small>{page.published ? 'Published' : 'Draft'} · /{page.slug}</small>
          </button>
        ))}
      </aside>

      <section aria-labelledby="admin-wiki-title" className="admin_details">
        <header className="admin-wiki__header">
          <div>
            <h1 id="admin-wiki-title">Wiki</h1>
            <p>Published pages are public and included in the sitemap.</p>
          </div>
        </header>

        {error && <p className="admin-changelog__error" role="alert">{error}</p>}

        <form className="admin-wiki__form" onSubmit={save}>
          <div className="admin-wiki__fields">
            <label>
              Page title (H1)
              <input
                maxLength="120"
                onChange={event => setDraft({
                  ...draft,
                  title: event.target.value,
                })}
                required
                value={draft.title}
              />
            </label>
            <label>
              Slug
              <input
                disabled={Boolean(draft.id)}
                maxLength="80"
                onChange={event => setDraft({
                  ...draft,
                  slug: event.target.value,
                })}
                pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                placeholder="getting-started"
                required
                value={draft.slug}
              />
              <small>Lowercase letters, numbers, and hyphens. It cannot change after creation.</small>
            </label>
          </div>

          <label className="admin-wiki__published">
            <input
              checked={draft.published}
              onChange={event => setDraft({
                ...draft,
                published: event.target.checked,
              })}
              type="checkbox"
            />
            Published
          </label>

          <div className="admin-wiki__blocks">
            <div className="admin-wiki__blocks-header">
              <h2>Page blocks</h2>
              <button
                className="button-secondary"
                disabled={saving}
                onClick={() => setDraft(current => ({
                  ...current,
                  blocks: [...current.blocks, emptyBlock()],
                }))}
                type="button"
              >
                Add block
              </button>
            </div>

            {draft.blocks.length === 0 && (
              <p className="admin-wiki__empty">Add at least one page block.</p>
            )}

            {draft.blocks.map((block, index) => (
              <fieldset className="admin-wiki__block" key={index}>
                <legend>Block {index + 1}</legend>
                <label>
                  Format
                  <select
                    onChange={event => updateBlock(index, {
                      type: event.target.value,
                      targetSlug: '',
                    })}
                    value={block.type}
                  >
                    {BLOCK_TYPES.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  {block.type === 'page-link' ? 'Link label' : 'Text'}
                  {block.type === 'text' ? (
                    <textarea
                      maxLength="5000"
                      onChange={event => updateBlock(index, {
                        text: event.target.value,
                      })}
                      required
                      rows="5"
                      value={block.text}
                    />
                  ) : (
                    <input
                      maxLength="160"
                      onChange={event => updateBlock(index, {
                        text: event.target.value,
                      })}
                      required
                      value={block.text}
                    />
                  )}
                </label>
                {block.type === 'page-link' && (
                  <label>
                    Target wiki page
                    <select
                      onChange={event => updateBlock(index, {
                        targetSlug: event.target.value,
                      })}
                      required
                      value={block.targetSlug}
                    >
                      <option value="">Select a page</option>
                      {linkedPages.map(page => (
                        <option key={page.slug} value={page.slug}>
                          {page.title} ({page.published ? 'published' : 'draft'})
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="admin-wiki__block-actions">
                  <button
                    aria-label={`Move block ${index + 1} up`}
                    className="button-secondary"
                    disabled={index === 0 || saving}
                    onClick={() => moveBlock(index, -1)}
                    type="button"
                  >
                    Up
                  </button>
                  <button
                    aria-label={`Move block ${index + 1} down`}
                    className="button-secondary"
                    disabled={index === draft.blocks.length - 1 || saving}
                    onClick={() => moveBlock(index, 1)}
                    type="button"
                  >
                    Down
                  </button>
                  <button
                    aria-label={`Remove block ${index + 1}`}
                    className="button-critical"
                    disabled={saving}
                    onClick={() => removeBlock(index)}
                    type="button"
                  >
                    Remove
                  </button>
                </div>
              </fieldset>
            ))}
          </div>

          <div className="admin-wiki__actions">
            <button className="button-primary" disabled={saving} type="submit">
              {saving ? 'Saving…' : draft.id ? 'Save page' : 'Create page'}
            </button>
            {draft.id && (
              <button
                className="button-critical"
                disabled={saving}
                onClick={remove}
                type="button"
              >
                Delete page
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
